import {
  CBadge,
  CButton,
  CButtonGroup,
  CCard,
  CCol,
  CDataTable,
  CFormGroup,
  CInput,
  CLabel,
  CListGroup,
  CListGroupItem,
  CRow,
  CSelect,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";

import { getItem, updateItem } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { RootState } from "../../store";
import StoreExchange, {
  StoreExchangeItem,
  getDeliveredItems,
  getReturnedItems,
  getExchangeDifferenceColor,
  getExchangeDifferenceLabel,
} from "../../models/store-exchange";
import User from "../../models/user";
import UserSelect from "../user/UserSelect";
import {
  EXCHANGE_REASONS,
  EXCHANGE_REASON_OTHER,
} from "../../utils/constants/exchange-reasons";
import { formatDateTime } from "../../utils/dates";
import { defaultValueOnUndefined } from "../../utils/fields";
import { formatToCurrency } from "../../currency/format";
import { PYG } from "../../currency/available-currencies";
import { successAlert, warningAlert } from "../utils/messages";

const lungorLogo = require("../../assets/img/lungorlogo.jpg");

interface StoreExchangeDetailProps {
  storeExchange?: StoreExchange;
  canRevert?: boolean;
  onCancel: () => void | Promise<void>;
  onReverted?: () => void | Promise<void>;
}

const itemFields = [
  { key: "amount", _classes: ["text-center"], label: "Cantidad" },
  { key: "productName", _classes: ["text-center"], label: "Producto" },
  { key: "variantName", _classes: ["text-center"], label: "Variante" },
  { key: "colorName", _classes: ["text-center"], label: "Color" },
  { key: "sizeName", _classes: ["text-center"], label: "Talle" },
  { key: "unitPrice", _classes: ["text-center"], label: "Precio unitario" },
  { key: "totalPrice", _classes: ["text-center"], label: "Subtotal" },
];

const StoreExchangeDetail: React.FC<StoreExchangeDetailProps> = ({
  storeExchange,
  canRevert,
  onCancel,
  onReverted,
}) => {
  const currentUser = useSelector((state: RootState) => state.user);

  const [exchange, setExchange] = useState<StoreExchange>(storeExchange || {});
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [revertUser, setRevertUser] = useState<User | undefined>(undefined);
  const [revertReason, setRevertReason] = useState<string>("");
  const [revertObs, setRevertObs] = useState<string>("");

  useEffect(() => {
    // Default the reverting user to the currently logged-in user.
    setRevertUser({
      id: currentUser.id,
      username: currentUser.username,
      email: currentUser.email,
    } as User);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExchange = async () => {
    if (storeExchange === undefined || storeExchange.id === undefined) {
      setExchange(storeExchange || {});
      return;
    }
    setLoading(true);
    const status = await getItem<StoreExchange>(
      `/store_exchanges/${storeExchange.id}/`
    );
    if (status.status === SUCCESS && status.data !== undefined) {
      setExchange(status.data);
    } else {
      setExchange(storeExchange);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchExchange();
    // Reset revert-form fields so values from a previously opened exchange
    // don't leak into the next one.
    setRevertReason("");
    setRevertObs("");
    setRevertUser({
      id: currentUser.id,
      username: currentUser.username,
      email: currentUser.email,
    } as User);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeExchange]);

  const onRevert = async () => {
    if (exchange.id === undefined) {
      return;
    }
    if (revertUser === undefined) {
      warningAlert("Debe seleccionar quién revierte el cambio");
      return;
    }
    if (revertReason === "") {
      warningAlert("Debe seleccionar un motivo de la reversión");
      return;
    }
    if (revertReason === EXCHANGE_REASON_OTHER && revertObs.trim() === "") {
      warningAlert(
        "Debe completar la observación cuando el motivo es 'Otros'"
      );
      return;
    }
    setReverting(true);
    const displayName = revertUser.username || revertUser.email;
    const status = await updateItem<StoreExchange>(
      `/store_exchanges/${exchange.id}/revert/`,
      {
        revertedById: revertUser.id,
        revertedByName: displayName,
        revertReason: revertReason,
        revertObs: revertObs.trim() !== "" ? revertObs.trim() : undefined,
      }
    );
    setReverting(false);
    if (status.status === SUCCESS) {
      successAlert("Cambio revertido correctamente");
      if (onReverted) {
        await onReverted();
      }
    } else {
      warningAlert(status.detail ? status.detail : "Error al revertir");
    }
  };

  if (loading) {
    return (
      <Spinner
        animation="grow"
        style={{
          height: "17px",
          width: "17px",
          marginTop: "auto",
          marginBottom: "auto",
          marginRight: "10px",
        }}
      />
    );
  }

  const returnedItems = getReturnedItems(exchange);
  const deliveredItems = getDeliveredItems(exchange);

  const priceTable = (items: StoreExchangeItem[]) => (
    <CDataTable
      noItemsView={<h6 className="text-center">Sin artículos</h6>}
      addTableClasses={"table-fixed"}
      fields={itemFields}
      items={items}
      striped
      border
      responsive
      scopedSlots={{
        productName: (item: StoreExchangeItem) => (
          <td className="text-center">
            <div>{defaultValueOnUndefined("-", item.productName)}</div>
            {item.reason ? (
              <small className="text-muted">
                {`Ajuste de precio realizado por: ${item.reason}`}
              </small>
            ) : null}
          </td>
        ),
        unitPrice: (item: StoreExchangeItem) => (
          <td className="text-center">
            {item.unitPrice ? formatToCurrency(item.unitPrice, PYG) : "-"}
          </td>
        ),
        totalPrice: (item: StoreExchangeItem) => (
          <td className="text-center">
            {item.totalPrice ? formatToCurrency(item.totalPrice, PYG) : "-"}
          </td>
        ),
      }}
    />
  );

  return (
    <fieldset disabled={loading}>
      <CRow className={"justify-content-center"}>
        <CCol lg="12">
          <CCard>
            <CRow>
              <CCol lg="12" className={"d-flex justify-content-around"}>
                <div className="p-3 align-middle">
                  <img
                    src={lungorLogo}
                    alt="logo"
                    style={{ height: "100%", maxHeight: "100px" }}
                  />
                </div>
                <div className="p-3 d-flex align-items-center">
                  <h2>Detalle de Cambio</h2>
                  {exchange.reverted ? (
                    <CBadge color={"danger"} className={"ml-2"}>
                      Cambio Revertido
                    </CBadge>
                  ) : null}
                </div>
                <div className="p-3 d-flex align-items-center">
                  <h5>
                    {exchange.exchangeDate
                      ? formatDateTime(new Date(exchange.exchangeDate))
                      : ""}
                  </h5>
                </div>
              </CCol>
            </CRow>
          </CCard>
        </CCol>
      </CRow>
      <CRow>
        <CCol lg="6">
          <CListGroup>
            <CListGroupItem>
              {`Sucursal: ${defaultValueOnUndefined("-", exchange.storeName)}`}
            </CListGroupItem>
            <CListGroupItem>
              {`Venta original: #${defaultValueOnUndefined(
                "-",
                exchange.originalSaleId
              )}`}
            </CListGroupItem>
            <CListGroupItem>
              {`Cliente: ${defaultValueOnUndefined(
                "SIN NOMBRE",
                exchange.clientName
              )}`}
            </CListGroupItem>
            <CListGroupItem>
              {`Usuario: ${defaultValueOnUndefined("-", exchange.sellerName)}`}
            </CListGroupItem>
            <CListGroupItem>
              {`Motivo: ${defaultValueOnUndefined("-", exchange.reason)}`}
            </CListGroupItem>
            {exchange.obs ? (
              <CListGroupItem>{`Observación: ${exchange.obs}`}</CListGroupItem>
            ) : null}
          </CListGroup>
        </CCol>
        <CCol lg="6">
          <CListGroup>
            <CListGroupItem>
              {`Total devuelto: ${
                exchange.returnedTotal !== undefined
                  ? formatToCurrency(exchange.returnedTotal, PYG)
                  : "-"
              }`}
            </CListGroupItem>
            <CListGroupItem>
              {`Total entregado: ${
                exchange.deliveredTotal !== undefined
                  ? formatToCurrency(exchange.deliveredTotal, PYG)
                  : "-"
              }`}
            </CListGroupItem>
            <CListGroupItem>
              <strong className={"mr-2"}>Diferencia de precio:</strong>
              <CBadge
                color={getExchangeDifferenceColor(exchange.priceDifference)}
              >
                {getExchangeDifferenceLabel(exchange.priceDifference)}
                {exchange.priceDifference !== undefined &&
                exchange.priceDifference !== 0
                  ? ` · ${formatToCurrency(
                      Math.abs(exchange.priceDifference),
                      PYG
                    )}`
                  : ""}
              </CBadge>
            </CListGroupItem>
            {exchange.reverted && exchange.revertedByName ? (
              <CListGroupItem>
                {`Revertido por: ${exchange.revertedByName}`}
              </CListGroupItem>
            ) : null}
            {exchange.reverted ? (
              <CListGroupItem>
                {`Motivo reversión: ${defaultValueOnUndefined(
                  "-",
                  exchange.revertReason
                )}`}
              </CListGroupItem>
            ) : null}
            {exchange.reverted && exchange.revertObs ? (
              <CListGroupItem>
                {`Obs reversión: ${exchange.revertObs}`}
              </CListGroupItem>
            ) : null}
            {exchange.cashRegisterOperationId ? (
              <CListGroupItem>
                {`Operación de caja: #${exchange.cashRegisterOperationId}`}
              </CListGroupItem>
            ) : null}
            {exchange.differenceSaleId ? (
              <CListGroupItem>
                {`Venta por diferencia: #${exchange.differenceSaleId}`}
              </CListGroupItem>
            ) : null}
          </CListGroup>
        </CCol>
      </CRow>
      <CRow className={"mt-3"}>
        <CCol lg="12">
          <h5>Artículos devueltos (ingresan a stock)</h5>
          {priceTable(returnedItems)}
        </CCol>
      </CRow>
      <CRow className={"mt-3"}>
        <CCol lg="12">
          <h5>Artículos entregados (salen de stock)</h5>
          {priceTable(deliveredItems)}
        </CCol>
      </CRow>

      {canRevert && !exchange.reverted ? (
        <CRow className={"mt-4"}>
          <CCol lg="12">
            <h5>Reversión del cambio</h5>
          </CCol>
          <CCol lg="4">
            <CFormGroup>
              <CLabel>Revertido por</CLabel>
              <UserSelect
                value={revertUser ? revertUser : null}
                onChange={(newUser: User | null) =>
                  setRevertUser(newUser !== null ? newUser : undefined)
                }
              ></UserSelect>
            </CFormGroup>
          </CCol>
          <CCol lg="4">
            <CFormGroup>
              <CLabel>Motivo de la reversión</CLabel>
              <CSelect
                value={revertReason}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRevertReason(e.target.value)
                }
              >
                <option value="">Seleccione un motivo</option>
                {EXCHANGE_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </CSelect>
            </CFormGroup>
          </CCol>
          <CCol lg="4">
            <CFormGroup>
              <CLabel>
                {`Observación${
                  revertReason === EXCHANGE_REASON_OTHER
                    ? " (obligatoria)"
                    : ""
                }`}
              </CLabel>
              <CInput
                type="text"
                value={revertObs}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRevertObs(e.target.value)
                }
                placeholder="Observación"
              ></CInput>
            </CFormGroup>
          </CCol>
        </CRow>
      ) : null}

      <CFormGroup className="float-right mt-3">
        <CButtonGroup>
          <CButton type="button" color="secondary" onClick={onCancel}>
            Atras
          </CButton>
          {canRevert && !exchange.reverted ? (
            <CButton
              type="button"
              color="danger"
              className="text-white"
              onClick={onRevert}
              disabled={reverting}
            >
              {reverting ? "Procesando..." : "Revertir Cambio"}
            </CButton>
          ) : null}
        </CButtonGroup>
      </CFormGroup>
    </fieldset>
  );
};

export default StoreExchangeDetail;
